import ActionButton from 'lib/src/components/button/ActionButton';
import ButtonRow from 'lib/src/components/button/ButtonRow';
import LinkButton from '@components/button/LinkButton';
import Table from 'lib/src/components/table/Table';
import useRoles from 'lib/src/hooks/useRoles';
import { userRoles } from 'lib/src/shared/enums/dropdownEnums';
import { siteNames } from 'lib/src/shared/enums/site';
import { findRoleMatch } from 'lib/src/utils/generic';
import { Dispatch, SetStateAction } from 'react';
import { User, userRoleValues } from '../../../types/shared/User';

const UsersTable: React.FC<UsersTableProps> = ({
    users,
    isFetching,
    setUserToEdit,
    setUserToEditPassword,
    setUserToDelete,
}) => {
    const roles = useRoles();
    const isSuperAdmin = roles.includes(userRoles.superAdmin);
    const columns = [
        {
            key: 1,
            heading: 'Name',
            getValue: (row: User) => `${row.firstName} ${row.lastName}`,
            getSort: (a: User, b: User) => {
                try {
                    return a.firstName.localeCompare(b.firstName);
                } catch (e) {
                    return 0;
                }
            },
            searchable: true,
        },
        {
            key: 2,
            heading: 'Email',
            getValue: (row: User) => row.email,
            searchable: true,
        },
        {
            key: 3,
            heading: 'Roles',
            getValue: (row: User) => row.roles.map(r => userRoleValues[r]).join(', '),
        },
        {
            key: 4,
            heading: 'Site Permissions',
            getValue: (row: User) => siteNames[row.site],
        },
        {
            key: 5,
            heading: '',
            getValue: (row: User) => {
                const isRoleMatch = isSuperAdmin || findRoleMatch(roles, row.roles);
                return (
                    <ButtonRow alignment="top">
                        <LinkButton color="green" icon="search" href={`/users/${row.id}`}>
                            View
                        </LinkButton>
                        {isRoleMatch && (
                            <>
                                <ActionButton
                                    color="yellow"
                                    icon="pencil"
                                    onClick={() => setUserToEdit(row.id)}
                                >
                                    Edit
                                </ActionButton>
                                <ActionButton
                                    color="grey"
                                    icon="pencil"
                                    onClick={() => setUserToEditPassword(row.id)}
                                >
                                    Edit Password
                                </ActionButton>
                                <ActionButton
                                    color="red"
                                    icon="times"
                                    onClick={() => setUserToDelete(row.id)}
                                >
                                    Remove
                                </ActionButton>
                            </>
                        )}
                    </ButtonRow>
                );
            },
        },
    ];

    return <Table columns={columns} rows={Object.values(users)} isLoading={isFetching} />;
};

interface UsersTableProps {
    users: Record<number, User>;
    isFetching: boolean;
    setUserToEdit: Dispatch<SetStateAction<number | null>>;
    setUserToEditPassword: Dispatch<SetStateAction<number | null>>;
    setUserToDelete: Dispatch<SetStateAction<number | null>>;
}

export default UsersTable;
